import React, { useState, useRef } from 'react';
import { Card, Form, Button, Input, message, Divider, Typography } from 'antd';
import { StylesProps, RouterProps } from 'src/library/types';
import { MOBILE_BREAK_POINT } from 'src/globals';
import GenericHoc from 'src/common/generic-hoc';
import { HocOptions } from 'src/common/generic-hoc.types';
import LogoUrl from 'src/assets/logos/shipsyLogo.png';
import { loginRoute } from 'src/routing/routeConstants';
import { forgotPassword } from 'src/api/auth';
import { loadInitialData } from 'src/common/actions/loadInitialData';

const { Text } = Typography;
const FormItem = Form.Item;

const styles = (theme: any) => ({
  mainDiv: {
    minHeight: '100vh',
    backgroundColor: theme.bodyBackground,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  header: {
    marginTop: '20px',
    color: '#2F2D3B',
    fontSize: 18,
  },
  cardDiv: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: {
      left: '20px',
      right: '20px',
      bottom: '20px',
    },
  },
  cardStyle: {
    width: '600px',
    border: 'none',
  },
  inputDiv: {
    display: 'flex',
    minWidth: '343px',
    marginTop: 20,
  },
  submitButton: {
    height: '40px',
    marginTop: '10px',
    width: '100%',
    maxWidth: '343px',
  },
  link: {
    color: '#165492',
    fontWeight: 600,
  },
  [`@media (max-width: ${MOBILE_BREAK_POINT}px)`]: {
    mainDiv: {
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      fontSize: 16,
    },
    cardStyle: {
      width: '100%',
      border: 'none',
    },
    inputDiv: {
      width: '100%',
    },
  },
});

interface Props extends StylesProps<ReturnType<typeof styles>>, RouterProps {
  dispatch: (fn: (dispatch: any, getState: any) => Promise<void>) => void;
  t: any;
}

const ForgotPassword: React.FC<Props> = (props: Props) => {
  const { classes, history, dispatch, t } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [showLinkMessage, setShowLinkMessage] = useState<boolean>(false);

  const handleSubmit = async () => {
    await form.validateFields();
    setLoading(true);
    const email = form.getFieldValue('email');
    const payload = {
      email,
    };
    const response = await forgotPassword(payload);
    if (response.isSuccess) {
      setShowLinkMessage(true);
    } else {
      message.error(response.errorMessage);
    }
  };

  return (
    <div className={classes.mainDiv}>
      <div className={classes.contentDiv}>
        {!showLinkMessage ?
          <Card className={classes.cardStyle} bordered={false}>
            <Form form={form} onFinish={handleSubmit} className="login-form">
              <div className={classes.cardDiv}>
                <img
                  style={{
                    height: 'auto',
                    maxWidth: '246px',
                    maxHeight: '100px',
                    objectFit: 'contain',
                  }}
                  src={window.localStorage.getItem('logoUrl') || LogoUrl}
                  alt="Shipsy"
                />
                <p className={classes.header}>{t('Forgot Password')}</p>
                <div className={classes.inputDiv}>
                  <FormItem
                    name='email'
                    rules={[
                        {
                            type: 'email',
                            message: t('Please enter valid email id'),
                        },
                        {
                            required: true,
                            message: t('Email must be present!'),
                        },
                    ]}
                    style={{ width: '100%' }}
                  >
                    <Input
                      size="large"
                      placeholder={t('Enter Email Address')}
                      style={{
                          width: '100%',
                      }}
                    />
                  </FormItem>
                </div>
                <Button
                  type="primary"
                  className={classes.submitButton}
                  htmlType="submit"
                  loading={loading}
                >
                  {t('Send Reset Password Email')}
                </Button>
              </div>
            </Form>
          </Card>
        :
          <Card className={classes.cardStyle} bordered title="Success">
            {t('Instructions to Reset Password has been mailed.')} <Button type="link" onClick={() => history.push(loginRoute)}>{t('Click Here')}</Button> {t('to login to the Vendor Portal')}
          </Card>}
      </div>
    </div>
  );
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
  },
  connectJss: {
    useJss: true,
    styleSheet: styles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(ForgotPassword);
